import { Field, Float, ID, InputType, ObjectType } from 'type-graphql'
import { modelOptions, Prop } from '@typegoose/typegoose'
import { BaseEntity } from '../../base/BaseEntity'
import { generateEntityId } from '../../generateEntityId'
import { ModelFamilyEnum } from './ModelFamilyEnum'

/**
 * Canonical USD cost of a model, per million input/output tokens. The engine always
 * prices in USD first; per-account currency conversion is applied via `CurrencyRate`.
 * Dual `@ObjectType`/`@InputType` (the codebase convention, like `Order`/`Product`) so the
 * class is reusable as both the query shape and a typed GraphQL input. CRUD is served by
 * `ModelRateResolver` via `BaseResolver`.
 */
@ObjectType()
@InputType('ModelRateInput')
@modelOptions({ schemaOptions: { timestamps: true, collection: 'aimodelrates' } })
export class ModelRate extends BaseEntity {
  @Field(() => ID)
  @Prop({ type: String, required: true, default: () => generateEntityId('mdrt') })
  declare _id: string

  /** Provider model identifier this rate applies to (e.g. `gpt-5-nano`). Unique — see ModelRateSchema. */
  @Field(() => String)
  @Prop({ type: String, required: true })
  model: string

  /**
   * Vendor + product line + major generation classification (e.g. `OpenAiGpt5`), read off
   * the `model` id's version token. Reporting only — never a routing input. No default: every
   * family assignment is an editorial call, so a row saved without one fails validation.
   */
  @Field(() => ModelFamilyEnum)
  @Prop({ type: String, enum: ModelFamilyEnum, required: true })
  family: ModelFamilyEnum

  @Field(() => Float)
  @Prop({ type: Number, required: true })
  inputUsdPerMillion: number

  @Field(() => Float)
  @Prop({ type: Number, required: true })
  outputUsdPerMillion: number

  @Field(() => Boolean)
  @Prop({ type: Boolean, required: true, default: true })
  active: boolean

  /** Plain-number percentage markup applied on top of this model's USD rate (`10` = 10%), matching `CurrencyRate.markupPct`'s convention. Unset when no per-model markup has been priced. */
  @Field(() => Float, { nullable: true })
  @Prop({ type: Number })
  markupPct?: number

  @Field(() => String, { nullable: true })
  @Prop({ type: String })
  notes?: string
}
